import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, errMsg } from "../lib/api";
import { normalizePhoneCz } from "../lib/bfu";
import { EmailHint, ResendCode } from "../components/BfuBits";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const Shell = ({ title, subtitle, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
    <div className="w-full max-w-md fade-in">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>AuditŠVP</h1>
          <p className="text-xs text-slate-500">Diagnostika ŠVP vůči požadavkům RVP PV</p>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        <div className="mt-5">{children}</div>
      </div>
    </div>
  </div>
);

export default function AuthPages({ mode }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", website: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [smsStep, setSmsStep] = useState(null);
  const [code, setCode] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register") {
      const norm = normalizePhoneCz(form.phone);
      if (!norm) {
        setError("Zkontrolujte prosím telefon — stačí ho napsat jako 777 123 456.");
        return;
      }
      form.phone = norm;
      setForm({ ...form });
    }
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password, website: form.website });
        setUser(data); navigate("/dashboard");
      } else if (mode === "register") {
        const { data } = await api.post("/public/register/start", form);
        setSmsStep({ intakeId: data.intake_id, phone: data.phone, devCode: data.dev_code || null });
        toast.success("Ověřovací kód byl odeslán na váš telefon");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        setSent(true);
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: params.get("token") || "", password: form.password });
        toast.success("Heslo změněno, přihlaste se novým heslem.");
        navigate("/login");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyRegistration = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/public/intake/verify", { intake_id: smsStep.intakeId, code });
      setUser(data.user);
      toast.success("Účet vytvořen — vítejte v AuditŠVP");
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (mode === "forgot" && sent) {
    return (
      <Shell title="Zkontrolujte e-mail" subtitle="Pokud je e-mail registrován, poslali jsme odkaz na obnovení hesla.">
        <Link to="/login" className="text-sm font-medium text-emerald-700 hover:text-emerald-900" data-testid="back-to-login-link">← Zpět na přihlášení</Link>
      </Shell>
    );
  }

  const titles = {
    login: ["Přihlášení", "Přihlaste se ke svému účtu"],
    register: ["Registrace", "Vytvořte si účet pro vaši mateřskou školu"],
    forgot: ["Zapomenuté heslo", "Zadejte e-mail, pošleme vám odkaz na obnovení"],
    reset: ["Nové heslo", "Zvolte si nové heslo"],
  };

  if (mode === "register" && smsStep) {
    return (
      <Shell title="Ověření telefonu" subtitle={`Zadejte 6místný kód, který jsme poslali na ${smsStep.phone}`}>
        <form onSubmit={verifyRegistration} className="space-y-4">
          {smsStep.devCode && (
            <p className="rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="register-dev-sms-code">
              DEV režim — ověřovací kód: <b style={{ fontFamily: "monospace" }}>{smsStep.devCode}</b>
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="smscode">Kód z SMS</Label>
            <Input id="smscode" required inputMode="numeric" maxLength={6} autoFocus data-testid="register-sms-code-input"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="h-16 text-center text-3xl tracking-[0.4em] font-mono" placeholder="······" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="auth-error-text">{error}</p>}
          <Button type="submit" disabled={loading || code.length < 6} className="h-14 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700" data-testid="register-verify-button">
            {loading ? "Pracuji…" : "Ověřit a vytvořit účet"}
          </Button>
          <ResendCode testId="register-resend-code" onResend={async () => {
            try {
              const { data } = await api.post("/public/register/start", form);
              setSmsStep({ intakeId: data.intake_id, phone: data.phone, devCode: data.dev_code || null });
              toast.success("Poslali jsme vám nový kód");
            } catch (err) { toast.error(errMsg(err)); }
          }} />
          <button type="button" onClick={() => setSmsStep(null)} className="w-full text-center text-xs text-slate-500 underline">Zpět na formulář</button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell title={titles[mode][0]} subtitle={titles[mode][1]}>
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <div className="space-y-1.5">
            <Label htmlFor="name">Jméno / název školy</Label>
            <Input id="name" autoFocus className="h-12 text-base" data-testid="auth-name-input" value={form.name} onChange={set("name")} placeholder="MŠ Sluníčko" />
          </div>
        )}
        {mode !== "reset" && (
          <div className="space-y-1.5">
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" required autoFocus={mode !== "register"} className="h-12 text-base" data-testid="auth-email-input" value={form.email} onChange={set("email")} placeholder="reditelka@skolka.cz" />
            <EmailHint email={form.email} onFix={(v) => setForm({ ...form, email: v })} />
          </div>
        )}
        {mode !== "forgot" && (
          <div className="space-y-1.5">
            <Label htmlFor="password">{mode === "reset" ? "Nové heslo" : "Heslo"}</Label>
            <Input id="password" type="password" required className="h-12 text-base" data-testid="auth-password-input" value={form.password} onChange={set("password")} />
          </div>
        )}
        {mode === "register" && (
          <div className="space-y-1.5">
            <Label htmlFor="phone">Telefon (ověříme SMS kódem)</Label>
            <Input id="phone" type="tel" required className="h-12 text-base" data-testid="auth-phone-input" value={form.phone} onChange={set("phone")}
              onBlur={() => { const n = normalizePhoneCz(form.phone); if (n) setForm({ ...form, phone: n }); }} placeholder="777 123 456" />
            <p className="text-xs text-slate-400">Stačí napsat číslo bez předvolby — doplníme ji za vás.</p>
          </div>
        )}
        <input type="text" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off"
          className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0" aria-hidden="true" data-testid="hp-field" />
        {error && <p className="text-sm text-red-600" data-testid="auth-error-text">{error}</p>}
        <Button type="submit" disabled={loading} className="h-14 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700" data-testid="auth-submit-button">
          {loading ? "Pracuji…" : titles[mode][0]}
        </Button>
      </form>
      {mode === "login" && (
        <div className="mt-4 flex justify-between text-sm">
          <Link to="/forgot-password" className="text-slate-500 hover:text-slate-800" data-testid="forgot-password-link">Zapomenuté heslo?</Link>
          <Link to="/register" className="font-medium text-emerald-700 hover:text-emerald-900" data-testid="go-register-link">Vytvořit účet</Link>
        </div>
      )}
      {mode !== "login" && (
        <div className="mt-4 text-sm">
          <Link to="/login" className="text-slate-500 hover:text-slate-800" data-testid="back-to-login-link">← Zpět na přihlášení</Link>
        </div>
      )}
    </Shell>
  );
}
